/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Thin client for POST /api/sms-send (Batch D P0-15a). Resolves the user's
// Firebase ID token, posts the body + recipient list, and surfaces per-
// recipient results so callers can show a "couldn't reach 1 of 3" toast
// without leaking Twilio's internal message strings.
//
// Lives outside the React layer so the same helper is callable from the
// WMH context, the overlay, the iOS native bridge fallback, and Vitest.

import type { TwilioErrorKind } from "./twilioRequest";
import { apiUrl } from "@/utils/apiUrl";

export interface SmsSendResult {
  to: string;
  status: "sent" | "failed";
  kind?: TwilioErrorKind;
  messageSid?: string;
  message?: string;
}

export interface SmsSendOutcome {
  ok: boolean;
  sent: number;
  failed: number;
  results: SmsSendResult[];
  /** Set when the request itself failed (network, 4xx/5xx with no body). */
  errorDetail?: string;
}

export interface SmsSendInput {
  /** One or more E.164 phone numbers. Server also normalizes; this keeps a
   *  symmetric guard so we never send obviously-invalid numbers. */
  to: string[];
  body: string;
  /** Resolves the caller's Firebase ID token. Injected so tests can supply
   *  a fake and so callers in different React subtrees can use the auth
   *  context they already have. */
  getIdToken: () => Promise<string | null>;
  /** Override the fetch implementation for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export async function sendWalkMeHomeSms(
  input: SmsSendInput,
): Promise<SmsSendOutcome> {
  const fetcher = input.fetchImpl ?? fetch;
  let idToken: string | null = null;
  try {
    idToken = await input.getIdToken();
  } catch (err) {
    return {
      ok: false,
      sent: 0,
      failed: input.to.length,
      results: [],
      errorDetail: `Could not get auth token: ${
        err instanceof Error ? err.message : String(err)
      }`,
    };
  }
  if (!idToken) {
    return {
      ok: false,
      sent: 0,
      failed: input.to.length,
      results: [],
      errorDetail: "Not signed in",
    };
  }

  try {
    const r = await fetcher(apiUrl("/api/sms-send"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ to: input.to, body: input.body }),
    });
    const text = await r.text();
    let parsed:
      | { results?: SmsSendResult[]; error?: string; detail?: string }
      | null = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!r.ok) {
      return {
        ok: false,
        sent: 0,
        failed: input.to.length,
        results: parsed?.results ?? [],
        errorDetail: parsed?.error ?? `HTTP ${r.status}`,
      };
    }
    const results = parsed?.results ?? [];
    const sent = results.filter((x) => x.status === "sent").length;
    const failed = results.length - sent;
    return {
      ok: failed === 0,
      sent,
      failed,
      results,
    };
  } catch (err) {
    return {
      ok: false,
      sent: 0,
      failed: input.to.length,
      results: [],
      errorDetail: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Send a single one-off SMS to one recipient. Thin alias over the generic
 * sendWalkMeHomeSms transport (which, despite its name, is feature-agnostic:
 * it just POSTs {to, body} to /api/sms-send and never throws). Exists so
 * callers like the emergency-contact opt-in confirmation read clearly instead
 * of calling a WMH-named function. Returns the same never-rejecting outcome.
 */
export function sendOneOffSms(
  input: Omit<SmsSendInput, "to"> & { to: string },
): Promise<SmsSendOutcome> {
  return sendWalkMeHomeSms({ ...input, to: [input.to] });
}

/** Compact summary string for toast / log lines. */
export function summarizeSmsOutcome(outcome: SmsSendOutcome): string {
  if (outcome.errorDetail && outcome.results.length === 0) {
    return `SMS failed: ${outcome.errorDetail}`;
  }
  if (outcome.ok) return `Sent to ${outcome.sent} contact${outcome.sent === 1 ? "" : "s"}`;
  return `Sent ${outcome.sent} of ${outcome.sent + outcome.failed}`;
}
